import { STORE_NAME } from '@intigral/constant';
import { useEffect, useRef } from 'react';
import { getGuestRedirectionUrl, isGuestUser } from '../config/guestMode';
import userHelper from '../helpers/user';
import { getUserInfo } from '../utils/user';

type CrossTabSessionSyncOptions = {
    /** True while this tab believes a session (signed-in or guest) is active. */
    enabled: boolean;
    /** Whether the in-memory session is a guest browse session. */
    isGuest?: boolean;
};

function resolveLogoutDestination(wasGuest: boolean): string {
    const logoutUrl = userHelper.getUserLogoutUrl();
    if (!wasGuest) {
        return logoutUrl;
    }
    return getGuestRedirectionUrl(logoutUrl) || logoutUrl;
}

/**
 * Keeps multi-tab auth in sync: when another tab clears `localStorage.user`
 * (logout), redirect this tab before stale in-memory session state triggers
 * authenticated API calls with missing credentials.
 */
export function useCrossTabSessionSync({ enabled, isGuest = false }: CrossTabSessionSyncOptions): void {
    const isGuestRef = useRef(isGuest);
    isGuestRef.current = isGuest;
    const redirectingRef = useRef(false);

    useEffect(() => {
        if (!enabled || typeof window === 'undefined') {
            return;
        }

        const redirectForClearedSession = (previousUserJson?: string | null) => {
            if (redirectingRef.current) {
                return;
            }
            redirectingRef.current = true;

            let wasGuest = isGuestRef.current;
            if (previousUserJson) {
                try {
                    wasGuest = isGuestUser(JSON.parse(previousUserJson));
                } catch {
                    // Keep in-memory guest flag when oldValue is unreadable.
                }
            }

            window.location.href = resolveLogoutDestination(wasGuest);
        };

        const onStorage = (event: StorageEvent) => {
            if (event.storageArea !== window.localStorage) {
                return;
            }
            if (event.key !== STORE_NAME.USER) {
                return;
            }
            // Cleared in another tab (removeItem / set to empty).
            if (event.newValue !== null) {
                return;
            }
            redirectForClearedSession(event.oldValue);
        };

        const onTabActive = () => {
            if (document.visibilityState && document.visibilityState !== 'visible') {
                return;
            }
            // Belt-and-suspenders: storage events can be missed; on focus, verify storage.
            if (!getUserInfo()?.username) {
                redirectForClearedSession(null);
            }
        };

        window.addEventListener('storage', onStorage);
        window.addEventListener('focus', onTabActive);
        document.addEventListener('visibilitychange', onTabActive);

        return () => {
            window.removeEventListener('storage', onStorage);
            window.removeEventListener('focus', onTabActive);
            document.removeEventListener('visibilitychange', onTabActive);
        };
    }, [enabled]);
}
